import React from "react"
import { Field } from "formik"

import CatchCard from "~/library/CatchCard"

import * as Yup from "yup"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import { Area, AreaType } from "~/types/sponsorship/Area"

const areaSchema = Yup.object({
  name: Yup.string().required("Area name is required"),
  areaType: Yup.string()
    .oneOf(["physical", "digital"], "Area type is required")
    .required("Area type is required"),
  maxQuantity: Yup.number()
    .nullable()
    .when("areaType", {
      is: "physical",
      then: (schema) =>
        schema
          .required("Max quantity is required for physical areas")
          .min(1, "Max quantity must be at least 1"),
      otherwise: (schema) => schema.nullable(),
    }),
})

interface AreaFormProps {
  onSubmit: (values: any) => void
  initialValues?: Area
  isEditing?: boolean
  onCancel?: () => void
  onDelete?: () => void
  showCancel?: boolean
  areaType?: AreaType
  hideAreaType?: boolean
}

const AreaForm: React.FC<AreaFormProps> = ({
  onSubmit,
  initialValues,
  isEditing = false,
  onCancel,
  onDelete,
  showCancel = true,
  areaType,
  hideAreaType = false,
}) => {
  const defaultValues = {
    name: "",
    areaType: areaType || ("physical" as const),
    maxQuantity: areaType === "digital" ? null : 1,
  }

  const handleFormSubmit = (values: any) => {
    const formData = {
      name: values.name,
      areaType: values.areaType,
      maxQuantity: values.areaType === "physical" ? values.maxQuantity : null,
    }
    onSubmit(formData)
  }

  return (
    <FormikWrapper
      onSubmit={handleFormSubmit}
      validationSchema={areaSchema}
      initialValues={isEditing ? initialValues : defaultValues}
    >
      <CatchCard>
        <FormikInput
          name="name"
          label="Area Name"
          placeholder="Stadium Banner A (Main Field)"
          required
          helpText=""
        />

        {!hideAreaType && (
          <div className="mb-3">
            <label className="form-label">
              Area Type <span className="text-danger">*</span>
            </label>
            <Field name="areaType">
              {({ field, form }: any) => (
                <div>
                  <div className="form-check">
                    <input
                      {...field}
                      type="radio"
                      className="form-check-input"
                      id="physical"
                      value="physical"
                      checked={field.value === "physical"}
                      onChange={() => {
                        form.setFieldValue("areaType", "physical")
                        form.setFieldValue("maxQuantity", 1)
                      }}
                    />
                    <label htmlFor="physical" className="form-check-label">
                      Physical
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      {...field}
                      type="radio"
                      className="form-check-input"
                      id="digital"
                      value="digital"
                      checked={field.value === "digital"}
                      onChange={() => {
                        form.setFieldValue("areaType", "digital")
                        form.setFieldValue("maxQuantity", null)
                      }}
                    />
                    <label htmlFor="digital" className="form-check-label">
                      Digital
                    </label>
                  </div>
                  <div className="form-text">
                    Physical areas have limited capacity, digital areas are unlimited
                  </div>
                </div>
              )}
            </Field>
          </div>
        )}

        {(hideAreaType ? areaType === "physical" : true) && (
          <Field name="areaType">
            {({ field }: any) =>
              (hideAreaType || field.value === "physical") && (
                <FormikInput
                  name="maxQuantity"
                  label="Max Quantity"
                  type="number"
                  placeholder="5"
                  required
                  helpText="Maximum number of sponsorships allowed in this area"
                  min={1}
                />
              )
            }
          </Field>
        )}
      </CatchCard>

      <div className="d-flex mt-4">
        {isEditing && onDelete && (
          <button type="button" className="btn btn-outline-danger" onClick={onDelete}>
            Delete Area
          </button>
        )}
        <div className="ms-auto">
          {showCancel && (
            <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>
              Cancel
            </button>
          )}
          <button type="submit" className="btn btn-primary">
            {isEditing ? "Update Area" : "Create Area"}
          </button>
        </div>
      </div>
    </FormikWrapper>
  )
}

export default AreaForm
